/**
 * PayAction 결제 컴포넌트
 *
 * 구조: 프로그레스 바 + 1개 카드 + 2개 체크박스 + 단일 CTA
 *
 * 핵심 원칙:
 * - onSuccess 콜백에 의존하지 않음 (새로고침 시 state 소멸)
 * - localStorage에 order 전체 저장 → 새로고침 복원 + 3초 폴링
 * - paid 감지 시 컴포넌트 내부에서 직접 router.push
 */

'use client';

import { useState, useEffect, useRef, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import { Copy, CheckCircle2, Loader2 } from 'lucide-react';
import { API_BASE_URL } from '@/lib/api';
import type { CalculateResponse } from '@/types';
import { buildPreviewHeadline } from '@/lib/previewHeadline';

const STORAGE_KEY = 'current_order';

/* ─── Types ─── */

interface PayActionPaymentProps {
  userEmail: string;
  userName: string;
  amount: number;
  inputData: Record<string, any>;
  onError?: (error: string) => void;
}

interface OrderData {
  order_id: string;
  memo: string;
  amount: number;
  bank_label?: string;
}

/* ─── Progress Steps ─── */

const STEPS = ['정보 입력', '결제', '완료'] as const;

function ProgressBar({ current }: { current: number }) {
  return (
    <div className="w-full mb-8">
      <div className="flex items-center justify-between relative">
        {/* connector line */}
        <div className="absolute top-4 left-0 right-0 h-0.5 bg-gray-200" />
        <div
          className="absolute top-4 left-0 h-0.5 bg-indigo-600 transition-all duration-500"
          style={{ width: `${(current / (STEPS.length - 1)) * 100}%` }}
        />

        {STEPS.map((label, i) => (
          <div key={label} className="flex flex-col items-center relative z-10">
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-colors duration-300 ${
                i < current
                  ? 'bg-indigo-600 text-white'
                  : i === current
                  ? 'bg-indigo-600 text-white ring-4 ring-indigo-100'
                  : 'bg-gray-200 text-gray-500'
              }`}
            >
              {i < current ? (
                <CheckCircle2 className="w-5 h-5" />
              ) : (
                i + 1
              )}
            </div>
            <span
              className={`mt-2 text-xs font-medium ${
                i <= current ? 'text-indigo-700' : 'text-gray-400'
              }`}
            >
              {label}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─── Toast ─── */

function Toast({ message, visible }: { message: string; visible: boolean }) {
  return (
    <div
      className={`fixed top-6 left-1/2 -translate-x-1/2 z-50 transition-all duration-300 ${
        visible
          ? 'opacity-100 translate-y-0'
          : 'opacity-0 -translate-y-4 pointer-events-none'
      }`}
    >
      <div className="flex items-center gap-2 bg-gray-900 text-white px-5 py-3 rounded-xl shadow-lg text-base font-medium">
        <CheckCircle2 className="w-5 h-5 text-green-400 shrink-0" />
        {message}
      </div>
    </div>
  );
}

/* ─── Main Component ─── */

export default function PayActionPayment({
  userEmail,
  userName,
  amount,
  inputData,
  onError,
}: PayActionPaymentProps) {
  const router = useRouter();
  const orderEndpoint = `${API_BASE_URL}/api/v1/payaction/order`;

  const [order, setOrder] = useState<OrderData | null>(null);
  const [status, setStatus] = useState<'idle' | 'creating' | 'waiting' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // 체크박스 상태
  const [checkedDeposit, setCheckedDeposit] = useState(false);
  const [checkedName, setCheckedName] = useState(false);

  // 토스트 상태
  const [toastMsg, setToastMsg] = useState('');
  const [toastVisible, setToastVisible] = useState(false);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const showToast = useCallback((msg: string) => {
    if (toastTimer.current) clearTimeout(toastTimer.current);
    setToastMsg(msg);
    setToastVisible(true);
    toastTimer.current = setTimeout(() => setToastVisible(false), 2000);
  }, []);

  // CTA 활성화 조건: 두 체크박스 모두 체크
  const ctaEnabled = checkedDeposit && checkedName;

  /* ─── paid 감지 → 결과 페이지 이동 ─── */

  const goToReport = useCallback((jobId: string) => {
    localStorage.removeItem(STORAGE_KEY);
    if (pollingRef.current) clearInterval(pollingRef.current);
    router.push(`/report/public/${jobId}`);
  }, [router]);

  /* ─── 폴링: 3초 간격 확인 ─── */

  const startPolling = useCallback((orderId: string) => {
    if (pollingRef.current) clearInterval(pollingRef.current);

    const poll = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/v1/payaction/order/${orderId}/status`);
        if (!res.ok) return;
        const data = await res.json();
        if (data.status === 'paid' && data.job_id) {
          goToReport(data.job_id);
        }
      } catch {
        // 네트워크 에러 → 다음 폴링에서 재시도
      }
    };

    poll();
    pollingRef.current = setInterval(poll, 3000);
  }, [goToReport]);

  /* ─── localStorage 저장/복원 ─── */

  const saveOrder = (data: OrderData) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  };

  const loadOrder = (): OrderData | null => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (parsed.order_id && parsed.memo) return parsed as OrderData;
      if (typeof parsed === 'string') {
        return { order_id: parsed, memo: '', amount: 0 };
      }
      return null;
    } catch {
      return null;
    }
  };

  /* ─── 마운트 시: localStorage에서 복원 + 폴링 시작 ─── */

  useEffect(() => {
    const saved = loadOrder();

    if (!saved) {
      const legacyId = localStorage.getItem('current_order_id');
      if (legacyId) {
        localStorage.removeItem('current_order_id');
        fetch(`${API_BASE_URL}/api/v1/payaction/order/${legacyId}/status`)
          .then(r => r.ok ? r.json() : null)
          .then(data => {
            if (data && data.status !== 'paid') {
              const restored: OrderData = {
                order_id: legacyId,
                memo: data.memo || '',
                amount: data.amount || 0,
                bank_label: data.bank_label || '',
              };
              saveOrder(restored);
              setOrder(restored);
              setStatus('waiting');
              startPolling(legacyId);
            } else if (data?.status === 'paid' && data.job_id) {
              goToReport(data.job_id);
            }
          })
          .catch(() => {});
        return;
      }
    }

    if (saved) {
      setOrder(saved);
      setStatus('waiting');

      if (!saved.memo) {
        fetch(`${API_BASE_URL}/api/v1/payaction/order/${saved.order_id}/status`)
          .then(r => r.ok ? r.json() : null)
          .then(data => {
            if (data?.memo) {
              const updated = {
                ...saved,
                memo: data.memo,
                amount: data.amount || saved.amount,
                bank_label: data.bank_label || saved.bank_label,
              };
              saveOrder(updated);
              setOrder(updated);
            }
          })
          .catch(() => {});
      }

      startPolling(saved.order_id);
    }

    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ─── 주문 생성 ─── */

  const createOrder = async () => {
    try {
      setStatus('creating');
      setErrorMessage('');

      const response = await fetch(orderEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_email: userEmail,
          user_name: userName,
          amount,
          input_json: inputData,
        }),
      });

      if (!response.ok) {
        const errorText = await response.text().catch(() => '');
        let errorDetail = '주문 생성 실패';
        try {
          const errorJson = JSON.parse(errorText);
          errorDetail = errorJson.detail || errorJson.message || errorDetail;
        } catch { /* ignore */ }
        throw new Error(errorDetail);
      }

      const data = await response.json();

      const orderData: OrderData = {
        order_id: data.order_id,
        memo: data.memo,
        amount: data.amount,
        bank_label: data.bank_label || '',
      };

      saveOrder(orderData);
      setOrder(orderData);
      setStatus('waiting');
      startPolling(data.order_id);
    } catch (err: any) {
      const msg = '주문 생성 실패, 잠시 후 다시 시도해주세요';
      setErrorMessage(msg);
      setStatus('error');
      onError?.(msg);
    }
  };

  /* ─── 수동 확인 핸들러 ─── */

  const handleManualCheck = async () => {
    const orderId = order?.order_id;
    if (!orderId) return;

    try {
      const res = await fetch(`${API_BASE_URL}/api/v1/payaction/order/${orderId}/status`);
      if (!res.ok) {
        showToast('상태 확인 실패. 잠시 후 다시 시도해주세요.');
        return;
      }
      const data = await res.json();
      if (data.status === 'paid' && data.job_id) {
        goToReport(data.job_id);
      } else {
        showToast('아직 입금이 확인되지 않았습니다. 1~3분 후 다시 시도해주세요.');
      }
    } catch {
      showToast('네트워크 오류. 잠시 후 다시 시도해주세요.');
    }
  };

  /* ─── 클립보드 복사 ─── */

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text).then(
      () => showToast('복사되었습니다'),
      () => showToast('복사에 실패했습니다. 직접 입력해주세요.')
    );
  };

  /* ═══════════════════════════════════════════
     렌더링
     ═══════════════════════════════════════════ */

  // idle: 결제 시작
  if (status === 'idle') {
    return (
      <div className="max-w-md mx-auto px-5">
        <ProgressBar current={0} />
        <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-8 text-center">
          <h2 className="text-xl font-bold text-gray-900 mb-2">결제하기</h2>
          <p className="text-base text-gray-500 mb-8">
            프리미엄 사주 리포트 &mdash; {amount.toLocaleString()}원
          </p>
          <button
            onClick={createOrder}
            className="w-full py-4 bg-indigo-600 text-white text-lg font-semibold rounded-xl hover:bg-indigo-700 active:scale-[0.98] transition-all"
          >
            결제 시작
          </button>
        </div>
      </div>
    );
  }

  // creating: 로딩
  if (status === 'creating') {
    return (
      <div className="max-w-md mx-auto px-5">
        <ProgressBar current={1} />
        <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-8 text-center">
          <Loader2 className="w-12 h-12 text-indigo-600 animate-spin mx-auto mb-4" />
          <p className="text-lg text-gray-700">주문 생성 중...</p>
        </div>
      </div>
    );
  }

  // error: 에러
  if (status === 'error') {
    return (
      <div className="max-w-md mx-auto px-5">
        <ProgressBar current={1} />
        <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-8 text-center">
          <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-2xl">!</span>
          </div>
          <h3 className="text-lg font-bold text-red-600 mb-2">결제 오류</h3>
          <p className="text-base text-gray-600 mb-6">{errorMessage}</p>
          <button
            onClick={createOrder}
            className="w-full py-4 bg-indigo-600 text-white text-lg font-semibold rounded-xl hover:bg-indigo-700 active:scale-[0.98] transition-all"
          >
            다시 시도
          </button>
        </div>
      </div>
    );
  }

  /* ─── waiting: 입금 대기 (메인 UI) ─── */

  const bankLabel = order?.bank_label || '';
  const depositorNameCode = order?.memo || '';
  const displayAmount = order?.amount || amount;

  return (
    <div className="max-w-md mx-auto px-5">
      <Toast message={toastMsg} visible={toastVisible} />

      {/* 프로그레스 바 */}
      <ProgressBar current={1} />

      {/* 결제 직전 개인화 요약 카드 */}
      {(() => {
        const calc = (inputData as any)?.saju_result as CalculateResponse | undefined;
        if (!calc) return null;
        const preview = buildPreviewHeadline(calc, 2026, userName);
        return (
          <div className="mb-4 bg-white rounded-2xl border border-slate-200 p-4 shadow-sm">
            <p className="text-xs font-semibold text-indigo-700">결제 직전 핵심 요약</p>
            <p className="mt-1 text-base font-bold text-slate-900 leading-snug">
              {preview.title}
            </p>
            <p className="mt-1 text-sm text-slate-600">
              {preview.subtitle}
            </p>
          </div>
        );
      })()}

      {/* 단일 카드 */}
      <div className="bg-white rounded-2xl shadow-md border border-gray-100 overflow-hidden">

        {/* 카드 헤더 */}
        <div className="bg-indigo-600 px-6 py-5">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-bold text-white">입금 안내</h2>
            <div className="flex items-center gap-2">
              <span className="relative flex h-2.5 w-2.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-300 opacity-75" />
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-400" />
              </span>
              <span className="text-sm text-indigo-100">입금 확인 중</span>
            </div>
          </div>
          <p className="text-indigo-200 text-sm mt-1">
            아래 정보를 확인 후 입금해 주세요
          </p>
        </div>

        {/* 카드 본문 */}
        <div className="px-6 py-6 space-y-5">

          {/* 입금 금액 */}
          <div className="text-center py-3 bg-gray-50 rounded-xl">
            <p className="text-sm text-gray-500 mb-1">입금 금액</p>
            <p className="text-3xl font-extrabold text-gray-900">
              {displayAmount.toLocaleString()}
              <span className="text-lg font-bold ml-0.5">원</span>
            </p>
          </div>

          {/* 입금 계좌 */}
          {bankLabel && (
            <div>
              <label className="block text-sm font-semibold text-gray-500 mb-2">
                입금 계좌
              </label>
              <div className="flex items-center justify-between bg-gray-50 rounded-xl px-4 py-3.5">
                <span className="text-base font-bold text-gray-900 select-all">
                  {bankLabel}
                </span>
                <button
                  onClick={() => copyToClipboard(bankLabel, '계좌')}
                  className="flex items-center gap-1.5 text-sm font-semibold text-indigo-600 hover:text-indigo-700 shrink-0 ml-3 px-3 py-1.5 bg-indigo-50 rounded-lg hover:bg-indigo-100 transition-colors"
                >
                  <Copy className="w-4 h-4" />
                  계좌 복사
                </button>
              </div>
            </div>
          )}

          {/* 입금자명 (memo) */}
          <div>
            <label className="block text-sm font-semibold text-gray-500 mb-2">
              입금자명 <span className="text-red-500">(필수)</span>
            </label>
            <div className="flex items-center justify-between bg-red-50 border-2 border-red-200 rounded-xl px-4 py-3.5">
              {depositorNameCode ? (
                <span className="text-2xl font-black text-red-600 tracking-wider select-all">
                  {depositorNameCode}
                </span>
              ) : (
                <span className="text-base text-gray-400">로딩 중...</span>
              )}
              <button
                onClick={() => depositorNameCode && copyToClipboard(depositorNameCode, '입금자명')}
                className="flex items-center gap-1.5 text-sm font-semibold text-red-600 hover:text-red-700 shrink-0 ml-3 px-3 py-1.5 bg-red-100 rounded-lg hover:bg-red-200 transition-colors"
              >
                <Copy className="w-4 h-4" />
                입금자명 복사
              </button>
            </div>
            <p className="text-xs text-red-500 font-medium mt-2 leading-relaxed">
              * 은행 이체 시 "받는 분에게 표시" 란에 위 입금자명을 정확히 입력하세요.
              <br />
              미입력 또는 오입력 시 입금 확인이 불가합니다.
            </p>
          </div>

          {/* 구분선 */}
          <hr className="border-gray-100" />

          {/* 체크박스 2개 */}
          <div className="space-y-3">
            <label className="flex items-start gap-3 cursor-pointer group">
              <input
                type="checkbox"
                checked={checkedDeposit}
                onChange={(e) => setCheckedDeposit(e.target.checked)}
                className="mt-0.5 w-5 h-5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer shrink-0"
              />
              <span className="text-base text-gray-700 group-hover:text-gray-900 leading-snug">
                위 계좌로 <strong>{displayAmount.toLocaleString()}원</strong>을 입금했습니다.
              </span>
            </label>

            <label className="flex items-start gap-3 cursor-pointer group">
              <input
                type="checkbox"
                checked={checkedName}
                onChange={(e) => setCheckedName(e.target.checked)}
                className="mt-0.5 w-5 h-5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer shrink-0"
              />
              <span className="text-base text-gray-700 group-hover:text-gray-900 leading-snug">
                입금자명을 <strong className="text-red-600">{depositorNameCode || '...'}</strong>(으)로 정확히 입력했습니다.
              </span>
            </label>
          </div>

          {/* CTA 버튼 */}
          <button
            onClick={handleManualCheck}
            disabled={!ctaEnabled}
            className={`w-full py-4 text-lg font-bold rounded-xl transition-all duration-200 ${
              ctaEnabled
                ? 'bg-indigo-600 text-white hover:bg-indigo-700 active:scale-[0.98] shadow-lg shadow-indigo-200'
                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }`}
          >
            입금 완료 확인 요청
          </button>

          {!ctaEnabled && (
            <p className="text-center text-xs text-gray-400">
              위 두 항목을 모두 체크하면 버튼이 활성화됩니다
            </p>
          )}
        </div>

        {/* 카드 푸터: 안내 */}
        <div className="bg-gray-50 px-6 py-5 space-y-3 border-t border-gray-100">
          <div className="flex items-start gap-2">
            <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 shrink-0" />
            <p className="text-sm text-gray-600">
              입금 확인 후 <strong>자동으로</strong> 결과 페이지로 이동됩니다.
            </p>
          </div>
          <div className="flex items-start gap-2">
            <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 shrink-0" />
            <p className="text-sm text-gray-600">
              이 페이지를 닫아도 괜찮습니다. 완료 시 이메일로 안내드립니다.
            </p>
          </div>
          <div className="mt-3 pt-3 border-t border-gray-200">
            <p className="text-xs text-gray-400 leading-relaxed">
              5분이 지나도 결과를 받지 못한 경우{' '}
              <a
                href="mailto:hello@sajuos.com"
                className="text-indigo-600 font-semibold underline underline-offset-2"
              >
                hello@sajuos.com
              </a>
              으로 이메일과 입금자명을 기재하여 문의해 주세요.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
